<script setup>
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import { deleteRequest, getRequest, postRequest } from "@/utils/request.js";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const cityOptions = ref([]);
const selectedDistrict = ref([]);

const columns = [
  {
    key: "realName",
    dataIndex: "realName",
    title: "姓名",
    width: "10%",
  },
  {
    key: "avatar",
    dataIndex: "avatar",
    title: "头像",
    width: "10%",
  },
  {
    key: "birthday",
    dataIndex: "birthday",
    title: "生日",
    width: "5%",
  },
  {
    key: "gender",
    dataIndex: "gender",
    title: "性别",
    width: "5%",
  },
  {
    key: "jobSeekerStatus",
    dataIndex: "jobSeekerStatus",
    title: "求职状态",
    width: "8%",
  },
  {
    key: "phone",
    dataIndex: "phone",
    title: "电话",
    width: "8%",
  },
  {
    key: "wechatNumber",
    dataIndex: "wechatNumber",
    title: "微信号",
    width: "5%",
  },
  {
    key: "workingTime",
    dataIndex: "workingTime",
    title: "参加工作时间",
    width: "10%",
  },
  {
    key: "address",
    dataIndex: "address",
    title: "地址",
    width: "15%",
  },
  {
    key: "status",
    dataIndex: "status",
    title: "启用状态",
    width: "5%",
  },
  {
    key: "viewCount",
    dataIndex: "viewCount",
    title: "查看",
    width: "5%",
  },
  {
    key: "viewedCount",
    dataIndex: "viewedCount",
    title: "被查看",
    width: "5%",
  },
  {
    key: "appliedCount",
    dataIndex: "appliedCount",
    title: "接收简历",
    width: "5%",
  },
  {
    key: "acceptedCount",
    dataIndex: "acceptedCount",
    title: "录取",
    width: "5%",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadCity = () => {
  getRequest("/city").then((res) => {
    if (res.success) {
      const cityData = res.data.map((province) => {
        const children = province.cityList.map((city) => {
          const { id, name } = city;
          return { id, name, children: city.districtList };
        });

        const { id, name } = province;
        return { id, name, children };
      });
      console.info("cityData", cityData);
      cityOptions.value = cityData;
    }
  });
};

const loadData = () => {
  loading.value = true;

  let [provinceId, cityId, districtId] = [];
  if (selectedDistrict.value) {
    [provinceId, cityId, districtId] = unref(selectedDistrict);
  }

  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign(
    {},
    unref(searchFormModel),
    {
      pageNo: current,
      pageSize,
    },
    { provinceId, cityId, districtIds: [districtId] },
  );

  getRequest("/statistics/jobSeeker", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const resetForm = () => {
  formRef.value.resetFields();
  selectedDistrict.value = [];
  loadData();
};

const handlePreview = (record) => {
  modalRef.value.title = record.realName + "的简历";
  modalRef.value.handleShow(record);
};

const handleOpen = (record) => {
  postRequest(`/jobSeeker/open/${record.jobSeekerId}`).then((response) => {
    if (response.success) {
      loadData();
    } else {
      message.error(response.message);
    }
  });
};

const handleClose = (record) => {
  postRequest(`/jobSeeker/close/${record.jobSeekerId}`).then((response) => {
    if (response.success) {
      loadData();
    } else {
      message.error(response.message);
    }
  });
};

const handleRemove = (record) => {
  deleteRequest(`/jobSeeker/${record.jobSeekerId}`).then((response) => {
    if (response.success) {
      loadData();
    } else {
      message.error(response.message);
    }
  });
};

onMounted(() => {
  loadCity();
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="姓名" name="realName">
              <a-input
                v-model:value.trim="searchFormModel.realName"
                placeholder="请输入姓名"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="性别" name="gender">
              <a-select
                v-model:value.trim="searchFormModel.gender"
                placeholder="请选择性别"
              >
                <a-select-option value="MALE"> 男</a-select-option>
                <a-select-option value="FEMALE"> 女</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="求职状态" name="jobSeekerStatus">
              <a-select
                v-model:value="searchFormModel.jobSeekerStatus"
                placeholder="请选择求职状态"
              >
                <a-select-option value="1">离职-随时到岗</a-select-option>
                <a-select-option value="2">在职-月内到岗</a-select-option>
                <a-select-option value="3">在职-考虑机会</a-select-option>
                <a-select-option value="4">在职-暂不考虑</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="地区" name="district">
              <a-cascader
                v-model:value="selectedDistrict"
                :options="cityOptions"
                :fieldNames="{
                  label: 'name',
                  value: 'id',
                }"
                change-on-select
                placeholder="请选择地区"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'realName'">
            {{ record[column.dataIndex] || "未完善信息用户" }}
          </template>
          <template v-if="column.dataIndex === 'avatar'">
            <a-avatar :size="80" :src="$imageUrl(record[column.dataIndex])" />
          </template>
          <template v-if="column.dataIndex === 'gender'">
            <template v-if="record[column.dataIndex] === 'MALE'"> 男</template>
            <template v-else-if="record[column.dataIndex] === 'FEMALE'">
              女
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'jobSeekerStatus'">
            <template v-if="record[column.dataIndex] === 1">
              离职-随时到岗
            </template>
            <template v-else-if="record[column.dataIndex] === 2">
              在职-月内到岗
            </template>
            <template v-else-if="record[column.dataIndex] === 3">
              在职-考虑机会
            </template>
            <template v-else-if="record[column.dataIndex] === 4">
              在职-暂不考虑
            </template>
          </template>

          <template v-if="column.dataIndex === 'address'">
            <div>
              {{ record.provinceName }}{{ record.cityName
              }}{{ record.districtName }}
            </div>
            <div>{{ record.address }}</div>
          </template>

          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green">启用</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">停用</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a type="link" @click="handlePreview(record)">查看简历</a>
            <a-divider type="vertical" />
            <template v-if="record['status']">
              <a-popconfirm
                :title="`确定要将【${record.realName}】停用吗？`"
                @confirm="handleClose(record)"
              >
                <a type="link">停用</a>
              </a-popconfirm>
            </template>
            <template v-else>
              <a-popconfirm
                :title="`确定要将【${record.realName}】启用吗？`"
                @confirm="handleOpen(record)"
              >
                <a type="link">启用</a>
              </a-popconfirm>
            </template>
            <a-divider type="vertical" />
            <a-popconfirm
              :title="`确定要将【${record.realName}】删除吗？`"
              @confirm="handleRemove(record)"
            >
              <a type="link">删除</a>
            </a-popconfirm>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<style scoped lang="scss"></style>
